import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { CreditCard, Wallet, Activity, Building2 } from 'lucide-react';
import {
  Sidebar,
  DashboardHeader,
  StatsCard,
  TransactionTable
} from '../components/ui';
import QuickActionsGrid from '../components/dashboard/QuickActionsGrid';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import { TopCompaniesChart, SuccessRateChart, FuelTypeDonut } from '../components/dashboard/DashboardCharts';
import { KeyLocations, SystemHealth, ActionableInsights } from '../components/dashboard/MonitoringWidgets';
import { RealTimeMonitoringKPI, RecentAlerts, QuickActionsRight } from '../components/dashboard/RightSidebar';
import CompanyPage from '../components/pages/CompanyPage';
import UsersPage from '../components/pages/UsersPage';
import ReportsPage from '../components/pages/ReportsPage';
import InvoicePage from '../components/pages/InvoicePage';
import CardActivityPage from '../components/pages/CardActivityPage';
import CardManagementPage from '../components/pages/CardManagementPage';
import VehicleManagementPage from '../components/pages/VehicleManagementPage';
import ProfilePage from '../components/pages/ProfilePage';
import SettingsPage from '../components/pages/SettingsPage';
import AnalyticsPage from '../components/pages/AnalyticsPage';
import { apiService, type AdminDashboardSummary, type AdminDashboardAnalytics } from '../services/api';
import '../components/dashboard/DashboardStyles.css';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeMenuItem, setActiveMenuItem] = useState('overview');
  const [showDashboard, setShowDashboard] = useState(true);
  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [analytics, setAnalytics] = useState<AdminDashboardAnalytics | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingSummary(true);
        const [summaryData, analyticsData] = await Promise.all([
          apiService.getDashboardSummary(),
          apiService.getAnalytics()
        ]);
        console.log('[Dashboard] Summary Data:', summaryData);
        console.log('[Dashboard] Analytics Data:', analyticsData);
        setSummary(summaryData);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoadingSummary(false);
      }
    };

    if (showDashboard) {
      fetchData();
    }
  }, [showDashboard]);

  useEffect(() => {
    const handleNavigate = ((e: CustomEvent<string>) => {
      setActiveMenuItem(e.detail);
      setShowDashboard(e.detail === 'overview');
    }) as EventListener;
    
    window.addEventListener('navigate-dashboard', handleNavigate);
    return () => {
      window.removeEventListener('navigate-dashboard', handleNavigate);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMenuItemClick = (item: string) => {
    setActiveMenuItem(item);
    setShowDashboard(item === 'overview');
  };

  return (
    <div className={`dashboard-layout ${showDashboard ? 'layout--premium' : ''}`}>
      <Sidebar
        activeItem={activeMenuItem}
        onItemClick={handleMenuItemClick}
      />

      <div className="main-container">
        <DashboardHeader
          user={user}
          onLogout={handleLogout}
        />

        <div className="dashboard-content">
          <main className="dashboard-main-area">
            {showDashboard ? (
              <div className="dashboard-grid-container">
                {/* Welcome Section */}
                <div className="dashboard-welcome-premium">
                  <h1>Welcome back, {user?.firstName || 'admin'}</h1>
                  <p>Here's what's happening with your fuel card operations today.</p>
                </div>

                {/* Stats Row */}
                <div className="dashboard-stats-row">
                  <StatsCard
                    title="Monthly Revenue"
                    value={isLoadingSummary || !summary ? '...' : `₦${(summary.monthlyRevenue ?? 0).toLocaleString()}`}
                    icon={<Wallet size={20} />}
                    color="green"
                    trend={{ label: 'Current Period', type: 'neutral' }}
                    footerMetrics={[
                      { label: 'Running Balance', value: `₦${(summary?.totalRunningBalance ?? 0).toLocaleString()}` }
                    ]}
                  />
                  <StatsCard
                    title="Total Transactions"
                    value={isLoadingSummary || !summary ? '...' : (summary.totalTransactions ?? 0).toLocaleString()}
                    icon={<Activity size={20} />}
                    color="blue"
                    trend={{ label: 'Platform Activity', type: 'neutral' }}
                    footerMetrics={[
                      { label: '', value: '' }
                    ]}
                  />
                  <StatsCard
                    title="Active Cards"
                    value={isLoadingSummary || !summary ? '...' : (summary.activeCards ?? 0).toLocaleString()}
                    icon={<CreditCard size={20} />}
                    color="purple"
                    trend={{ label: 'Usage', type: 'neutral' }}
                    footerMetrics={[
                      { label: 'Total Issued', value: summary?.totalCardsIssued?.toString() || '0' },
                      { label: 'Suspended', value: summary?.suspendedCards?.toString() || '0' }
                    ]}
                  />
                  <StatsCard
                    title="Active Companies"
                    value={isLoadingSummary || !summary ? '...' : (summary.activeCompanies ?? 0).toLocaleString()}
                    icon={<Building2 size={20} />}
                    color="red"
                    trend={{ label: 'Client Base', type: 'neutral' }}
                    footerMetrics={[
                      { label: 'Total Companies', value: summary?.totalCompanies?.toString() || '0' }
                    ]}
                  />
                </div>

                {/* Quick Actions */}
                <QuickActionsGrid onNavigate={handleMenuItemClick} />

                <div className="main-dashboard-grid">
                  <div className="dashboard-left-column">
                    <ActivityFeed data={analytics?.realTimeActivity} />

                    <div className="grid-row-double">
                      <TopCompaniesChart data={analytics?.topCompaniesBySpending} />
                      <FuelTypeDonut data={analytics?.fuelTypeDistribution} />
                    </div>

                    <SuccessRateChart data={analytics?.transactionSuccessRate} />

                    <div className="grid-row-double">
                      <SystemHealth />
                      <ActionableInsights data={analytics?.actionableInsights} />
                    </div>

                    <div className="transaction-logs-container">
                      <div className="transaction-logs-header">
                        <h3 className="section-title-small">Transaction Logs</h3>
                        <div className="utility-buttons">
                          <button className="utility-btn">Export CSV</button>
                          <button className="utility-btn">Print</button>
                        </div>
                      </div>
                      <TransactionTable hideHeader={true} />
                    </div>
                  </div>

                  <aside className="dashboard-right-column">
                    <RealTimeMonitoringKPI data={analytics?.kpis} isLoading={isLoadingSummary} />
                    <KeyLocations data={analytics?.keyLocations} />
                    <RecentAlerts data={analytics?.recentAlerts} />
                    <QuickActionsRight />
                  </aside>
                </div>
              </div>
            ) : activeMenuItem === 'companies' ? (
              <CompanyPage />
            ) : activeMenuItem === 'staff' || activeMenuItem === 'users' ? (
              <UsersPage />
            ) : activeMenuItem === 'accounts' ? (
              <div className="component-container"><h1>Accounts Page</h1><p>Content coming soon</p></div>
            ) : activeMenuItem === 'card-management' ? (
              <CardManagementPage />
            ) : activeMenuItem === 'vehicles' ? (
              <VehicleManagementPage />
            ) : activeMenuItem === 'card-activity' ? (
              <CardActivityPage />
            ) : activeMenuItem === 'invoices' ? (
              <InvoicePage />
            ) : activeMenuItem === 'reports' ? (
              <ReportsPage />
            ) : activeMenuItem === 'analytics' ? (
              <AnalyticsPage />
            ) : activeMenuItem === 'settings' ? (
              <SettingsPage />
            ) : activeMenuItem === 'profile' ? (
              <ProfilePage />
            ) : (
              <div className="component-container">
                <h1>{activeMenuItem} Page</h1>
                <p>Content for {activeMenuItem} goes here.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
